import type React from "react";
import { useEffect, useMemo, useRef, useState } from "react";

import { Button, Col, Container, Dropdown, Form, Row, Spinner } from "react-bootstrap";

import { Controller, type Resolver, useForm } from "react-hook-form";
import { FaTimes } from "react-icons/fa";

import { type ArtistResaleInput, ParticipantTypes, type ResaleInput, useSearchByArtistNameQuery } from "@api/graphql";
import { ArtistAliveDisplay, years } from "@util/index";

import CountryDropdown from "../form/CountryDropdown";
import { FormErrorText } from "../form/FormErrorText";
import FormNavigationButtons from "../form/FormNavigationButtons";
import FormsHeader from "../form/FormsHeader";
import type { FormStepProps } from "../form/MultiStepForm";

import styles from "./ArtistDetails.module.scss";

type ValidationError = {
  type: "required";
  message: string;
};

type ValidationErrors = {
  [K in keyof ArtistResaleInput]?: ValidationError;
};

interface ArtistDetailsProps extends FormStepProps<ResaleInput, ArtistResaleInput> {
  isInsertMode?: boolean;
  submitRef: React.RefObject<HTMLButtonElement>;
  onValidationStatus?: (isValid: boolean) => void;
}

export const ArtistDetails = ({
  initialFormData,
  onSubmit,
  isInsertMode = true,
  submitRef,
  onValidationStatus,
}: ArtistDetailsProps) => {
  const inputRef = useRef<HTMLDivElement | null>(null);
  const dropdownRef = useRef<HTMLSelectElement | null>(null);

  const handleOutsideClick = (event: MouseEvent) => {
    if (
      inputRef.current &&
      !inputRef.current.contains(event.target as Node) &&
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node)
    ) {
      setShowDropdown(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);

  const resolver: Resolver<ArtistResaleInput> = (values) => {
    const errors: ValidationErrors = {};

    if (!values.provisional_artist_name) {
      errors.provisional_artist_name = {
        type: "required",
        message: "Artist name is required",
      };
    }
    if (!values.provisional_artist_country) {
      errors.provisional_artist_country = {
        type: "required",
        message: "Country is required",
      };
    }
    if (!provisionalArtistAliveSelected) {
      errors.provisional_is_artist_alive = {
        type: "required",
        message: "Living status is required",
      };
    }
    if (
      values.provisional_is_artist_alive === false &&
      !provisionalArtistYodUnknown &&
      provisionalArtistAliveSelected &&
      !values.provisional_artist_yod
    ) {
      errors.provisional_artist_yod = {
        type: "required",
        message: "Year of death or 'Unknown' is required if the artist is not living",
      };
    }

    const isValid = Object.keys(errors).length === 0;
    if (onValidationStatus) {
      onValidationStatus(isValid);
    }

    return {
      values: isValid ? values : {},
      errors: errors,
    };
  };

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    reset,
    formState: { errors },
    trigger,
  } = useForm<ArtistResaleInput>({
    resolver,
    mode: "onChange",
    reValidateMode: "onChange",
  });
  const [provisionalArtistYobUnknown, setProvisionalArtistYobUnknown] = useState(false);
  const [provisionalArtistYodUnknown, setProvisionalArtistYodUnknown] = useState(false);
  const [provisionalArtistAliveSelected, setProvisionalArtistAliveSelected] = useState(false);

  const [isInitialRender, setIsInitialRender] = useState(true);

  const [hasFormBeenReset, setHasFormBeenReset] = useState(false);

  // Since provisionalArtistAliveSelected is a state hook that updates asynchronously, it causes is artist alive field validation with a stale value triggering unrequired error for this field.
  // Therefore creating this useffect hook that runs each time provisionalArtistAliveSelected changes and forces validation with most up to date value except when the form is loaded (initial render when the user has not interacted with the form yet).
  useEffect(() => {
    if (isInitialRender) {
      setIsInitialRender(false);
    } else {
      trigger("provisional_is_artist_alive");
    }
    // Adding isInitialRender to dependency array will cause this hook to run a second time therefore triggering is artist alive field validation and causing unnecessary error.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provisionalArtistAliveSelected, trigger]);

  const [showDropdown, setShowDropdown] = useState(true);
  const [isDisabled, setIsDisabled] = useState(false);

  const artistName = watch("provisional_artist_name");

  const queryVariables = useMemo(() => ({ partialName: artistName || "" }), [artistName]);

  const { data, loading, refetch } = useSearchByArtistNameQuery({
    variables: queryVariables,
    skip: !artistName || queryVariables.partialName.length < 3,
  });
  useEffect(() => {
    if (artistName) {
      refetch({ partialName: artistName }).then(() => {});
    }
  }, [artistName, refetch]);

  const resultsToDisplay = useMemo(() => {
    return data?.searchByArtistName
      .reduce(
        (acc, item) => {
          const pseudonyms = Array.isArray(item.pseudonyms) && item.pseudonyms.length > 0 ? item.pseudonyms : undefined;
          if (!pseudonyms) {
            return [
              ...acc,
              {
                ...item,
                pseudonym: "",
                company_name: item.company_name,
                isCollaboration: item.participant_types?.includes(ParticipantTypes.COLLABORATION),
              },
            ];
          } else {
            return [
              ...acc,
              ...pseudonyms.map((pseudonym) => ({
                ...item,
                pseudonym,
                company_name: item.company_name,
                isCollaboration: item.participant_types?.includes(ParticipantTypes.COLLABORATION),
              })),
            ];
          }
        },
        [] as {
          participant_id: number;
          full_name?: string | undefined;
          pseudonym: string;
          citizenship_country?: string | undefined;
          year_of_birth?: number | undefined;
          deceased?: boolean | undefined;
          year_of_death?: number | undefined;
          company_name?: string | undefined;
          isCollaboration: boolean;
        }[],
      )
      .slice(0, 9);
  }, [data]);

  // setting up the form with the artist details in the multiFormData
  useEffect(() => {
    if (initialFormData && Object.keys(initialFormData).length > 0) {
      setValue("artist_id", initialFormData.artist_id);
      setValue("provisional_artist_name", initialFormData.provisional_artist_name);
      setValue("provisional_artist_country", initialFormData.provisional_artist_country);
      setValue("provisional_artist_yob", initialFormData.provisional_artist_yob);
      setValue("provisional_artist_yod", initialFormData.provisional_artist_yod);
      setValue("provisional_is_artist_alive", initialFormData.provisional_is_artist_alive);

      setProvisionalArtistAliveSelected(true);
      setShowDropdown(false);
      setProvisionalArtistYobUnknown(initialFormData.provisional_artist_yob == null);
      setProvisionalArtistYodUnknown(initialFormData.provisional_artist_yod == null);
      setIsDisabled(initialFormData.artist_id != null);
    }
  }, [initialFormData, setValue]);

  const handleSelectArtist = (fullName?: string) => {
    const selectedArtist = resultsToDisplay?.find((artist) => {
      return artist?.isCollaboration ? artist.company_name === fullName : artist.full_name === fullName;
    });
    if (selectedArtist) {
      if (selectedArtist.isCollaboration) {
        selectedArtist.company_name && setValue("provisional_artist_name", selectedArtist.company_name);
      } else {
        selectedArtist.full_name && setValue("provisional_artist_name", selectedArtist.full_name);
      }
      setValue("provisional_artist_country", selectedArtist.citizenship_country);
      setValue("provisional_artist_yob", selectedArtist.year_of_birth);
      setValue("provisional_artist_yod", selectedArtist.year_of_death);
      setValue("artist_id", Number(selectedArtist.participant_id));
      setValue(
        "provisional_is_artist_alive",
        selectedArtist.deceased === true ? false : selectedArtist.deceased === false ? true : undefined,
      );
      setHasFormBeenReset(false);

      setProvisionalArtistYobUnknown(selectedArtist.year_of_birth == null);
      setProvisionalArtistYodUnknown(selectedArtist.year_of_death == null);

      setShowDropdown(false);
      setIsDisabled(true);
      setProvisionalArtistAliveSelected(true);
    }
  };

  const handleArtistNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setValue("provisional_artist_name", value);
    if (!value) {
      emptyForm();
    }
    setShowDropdown(true);
  };

  const emptyForm = () => {
    reset({
      provisional_artist_name: "",
      provisional_artist_country: "",
      provisional_artist_yob: undefined,
      provisional_artist_yod: undefined,
      provisional_is_artist_alive: undefined,
    });
    setHasFormBeenReset(true);
    setProvisionalArtistYobUnknown(false);
    setProvisionalArtistYodUnknown(false);
    setIsDisabled(false);
    setShowDropdown(true);
    setValue("artist_id", undefined);
  };

  return (
    <Form>
      <Container>
        <FormsHeader isInsertMode={isInsertMode} title="Artist details" />
        <Row className="mb-4">
          <Col sm={12} md={6}>
            <Form.Group controlId="artistName">
              <Form.Label>
                * Artist name
                {loading && (
                  <Spinner animation="border" role="status" size="sm" className="ms-2">
                    <span className="visually-hidden">Loading...</span>
                  </Spinner>
                )}
              </Form.Label>
              <div ref={inputRef} className="input-group border border-1 border-gray-300 rounded">
                <Form.Control
                  {...register("provisional_artist_name")}
                  type="text"
                  placeholder="Enter name"
                  onChange={handleArtistNameChange}
                  disabled={isDisabled}
                  className="border-0"
                />
                {isDisabled && (
                  <Button
                    variant="link"
                    onClick={() => {
                      emptyForm();
                    }}
                  >
                    <FaTimes />
                  </Button>
                )}
              </div>
              <FormErrorText error={errors.provisional_artist_name} />
              {
                <Dropdown
                  show={resultsToDisplay && resultsToDisplay?.length > 0 && showDropdown}
                  onToggle={(e) => setShowDropdown(e)}
                  onSelect={(e) => handleSelectArtist(e!)}
                >
                  <Dropdown.Menu className={styles["dropdown"]}>
                    {resultsToDisplay?.map((artist) => (
                      <Dropdown.Item
                        key={`${artist.full_name}-${artist.pseudonym}`}
                        className="py-3"
                        eventKey={artist.isCollaboration ? artist.company_name : artist.full_name}
                      >
                        <div className="d-flex flex-column">
                          {artist.isCollaboration ? (
                            <div className="d-flex align-items-center">
                              <div
                                style={{
                                  minWidth: "7rem",
                                }}
                              >
                                Company Name:
                              </div>
                              <div>{artist.company_name}</div>
                            </div>
                          ) : (
                            <>
                              <div className="d-flex align-items-center">
                                <div style={{ minWidth: "7rem" }}>Name:</div>
                                <div>{artist.full_name}</div>
                              </div>
                              <div className="d-flex align-items-center">
                                <div style={{ minWidth: "7rem" }}>Pseudonym:</div>
                                <div>{artist.pseudonym || "-"}</div>
                              </div>
                            </>
                          )}
                        </div>
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
              }
            </Form.Group>
          </Col>
          <Col md={6} sm={12}>
            <CountryDropdown
              fieldName="provisional_artist_country"
              control={control}
              isDisabled={isDisabled}
              isRequired
              errors={errors}
            />
          </Col>
        </Row>
        <Row className="mb-4">
          <Col>
            <Form.Group>
              <Form.Label>* Is the artist living?</Form.Label>
              <div key={"inline-radio"}>
                <Row>
                  <Col className="mb-1" sm={6}>
                    <Controller
                      name="provisional_is_artist_alive"
                      control={control}
                      render={({ field }) => (
                        <Form.Check
                          type="radio"
                          id="living-yes"
                          label={ArtistAliveDisplay.YES}
                          value="yes"
                          checked={field.value === true}
                          onChange={() => {
                            field.onChange(true);
                            setProvisionalArtistAliveSelected(true);
                            setValue("provisional_artist_yod", undefined); // Clear Year of Death
                            setProvisionalArtistYodUnknown(false);
                          }}
                          disabled={isDisabled}
                        />
                      )}
                    />
                  </Col>
                  <Col className="mb-1" sm={6}>
                    <Controller
                      name="provisional_is_artist_alive"
                      control={control}
                      render={({ field }) => (
                        <Form.Check
                          type="radio"
                          id="living-no"
                          label={ArtistAliveDisplay.NO}
                          value="no"
                          checked={field.value === false}
                          onChange={() => {
                            field.onChange(false);
                            setProvisionalArtistAliveSelected(true);
                          }}
                          disabled={isDisabled}
                        />
                      )}
                    />
                  </Col>
                  <Col className="mb-1" sm={6}>
                    <Controller
                      name="provisional_is_artist_alive"
                      control={control}
                      render={({ field }) => (
                        <Form.Check
                          type="radio"
                          id="living-unknown"
                          label={ArtistAliveDisplay.UNKNOWN}
                          value="unknown"
                          checked={provisionalArtistAliveSelected && field.value === undefined && !hasFormBeenReset}
                          onChange={() => {
                            field.onChange(undefined);
                            setHasFormBeenReset(false);
                            setProvisionalArtistAliveSelected(true);
                            setValue("provisional_artist_yod", undefined); // Clear Year of Death
                            setProvisionalArtistYodUnknown(false);
                          }}
                          disabled={isDisabled}
                        />
                      )}
                    />
                  </Col>
                </Row>
              </div>
              <FormErrorText error={errors.provisional_is_artist_alive} />
            </Form.Group>
          </Col>
        </Row>
        <Row className="mb-4">
          <Col md={6} sm={12}>
            <Form.Group controlId="yearOfBirth">
              <Form.Label>Year of birth</Form.Label>
              <Controller
                name="provisional_artist_yob"
                control={control}
                render={({ field }) => (
                  <Form.Select
                    {...field}
                    className="mb-1"
                    disabled={isDisabled || provisionalArtistYobUnknown}
                    onChange={(e) => {
                      const value = e.target.value ? Number.parseInt(e.target.value, 10) : undefined;
                      field.onChange(value);
                      setProvisionalArtistYobUnknown(false);
                    }}
                    value={field.value || ""}
                  >
                    <option value="">...</option>
                    {years.map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </Form.Select>
                )}
              />
              <Form.Check
                className="mb-1"
                type="switch"
                id="birth-unknown"
                label="Unknown"
                onChange={(e) => {
                  setProvisionalArtistYobUnknown(e.target.checked);
                  if (e.target.checked) {
                    setValue("provisional_artist_yob", undefined);
                  }
                }}
                checked={provisionalArtistYobUnknown}
                disabled={isDisabled || !!watch("provisional_artist_yob")}
              />
            </Form.Group>
          </Col>
          <Col md={6} sm={12}>
            <Form.Group controlId="yearOfDeath">
              <Form.Label>Year of death</Form.Label>
              <Controller
                name="provisional_artist_yod"
                control={control}
                render={({ field }) => (
                  <Form.Select
                    {...field}
                    className="mb-1"
                    disabled={
                      isDisabled ||
                      provisionalArtistYodUnknown ||
                      watch("provisional_is_artist_alive") === true ||
                      watch("provisional_is_artist_alive") === undefined
                    }
                    onChange={(e) => {
                      const value = e.target.value ? Number.parseInt(e.target.value, 10) : undefined;
                      field.onChange(value);
                      setProvisionalArtistYodUnknown(false);
                    }}
                    value={field.value || ""}
                  >
                    <option value="">...</option>
                    {years.map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </Form.Select>
                )}
              />

              <Form.Check
                className="mb-1"
                type="switch"
                id="death-unknown"
                label="Unknown"
                onChange={(e) => {
                  setProvisionalArtistYodUnknown(e.target.checked);
                  if (e.target.checked) {
                    setValue("provisional_artist_yod", undefined);
                  }
                }}
                checked={provisionalArtistYodUnknown}
                disabled={
                  isDisabled ||
                  !!watch("provisional_artist_yod") ||
                  watch("provisional_is_artist_alive") === true ||
                  watch("provisional_is_artist_alive") === undefined
                }
              />
              <FormErrorText error={errors.provisional_artist_yod} />
            </Form.Group>
          </Col>
        </Row>
        <FormNavigationButtons
          isInsertMode={isInsertMode}
          onSubmit={onSubmit}
          handleSubmit={handleSubmit}
          showPreviousButton={false}
          submitRef={submitRef}
        />
      </Container>
    </Form>
  );
};
